import { CreateVersionModal } from '@/components/CreateVersionModal/CreateVersionModal'
import { GridSideControllerContainer } from '@/components/GridSideControllerContainer'
import { SidePanel } from '@/components/SidePanel'
import { useCreateGrid } from '@/queries/grids/useCreateGrid'
import { useCreateGridVersion } from '@/queries/grids/useCreateGridVersion'
import { useGrid } from '@/queries/grids/useGrid'
import { useGridVersions } from '@/queries/grids/useGridVersions'
import { usePromoteGridVersion } from '@/queries/grids/usePromoteGridVersion'
import { useUpdateGrid } from '@/queries/grids/useUpdateGrid'
import {
	gridDataSelector,
	resetGrid,
	selectedVersionIdSelector,
	setGrid,
	versionSelector,
} from '@/redux/store/slices/grid/gridSlice'
import { useAppDispatch, useAppSelector } from '@redux/hooks/useAppState'
import { useStytchOrganization } from '@stytch/react/b2b'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

export const GridDetails = () => {
	const { workspaceId, gridId: gridIdParam = '' } = useParams()
	const { organization } = useStytchOrganization()
	const [searchParams] = useSearchParams()
	// When on the /add route, gridIdParam will be "add", so prioritize query param
	const gridId = gridIdParam === 'add' ? searchParams.get('gridId') || '' : gridIdParam
	const isAdding = window.location.pathname.includes('/add')
	const navigate = useNavigate()
	const { enqueueSnackbar } = useSnackbar()
	const [isCreateVersionModalOpen, setIsCreateVersionModalOpen] = useState(false)

	// Make sure cache is populated and updated
	const { data: apiGrid } = useGrid({
		workspaceId: organization?.organization_id,
		gridId: gridId,
		include: 'currentVersion',
		enabled: !isAdding && !!gridId, // Don't fetch when adding new grid
	})

	const { data: versions = [] } = useGridVersions({
		workspaceId: organization?.organization_id,
		gridId: gridId,
		enabled: !isAdding && !!gridId,
	})

	const dispatch = useAppDispatch()
	const { mutateAsync: updateGrid } = useUpdateGrid()
	const { mutateAsync: createGrid } = useCreateGrid()
	const { mutateAsync: createGridVersion } = useCreateGridVersion()
	const { mutateAsync: promoteGridVersion } = usePromoteGridVersion()
	const grid = useAppSelector(gridDataSelector)
	const draftVersion = useAppSelector(versionSelector)
	const selectedVersionId = useAppSelector(selectedVersionIdSelector)

	// Determine if only grid metadata has changed (vs version data)
	const hasGridMetadataChanges = () => {
		if (!apiGrid || !grid) return false
		return apiGrid.name !== grid.name
		// Add other grid-level fields here if needed
	}

	const hasVersionChanges = () => {
		if (!draftVersion) return false

		// For new grids (adding), compare against original API data if available
		// For existing grids, compare against the saved version from API
		const savedVersion = apiGrid?.currentVersion
		if (!savedVersion) return false

		return (
			JSON.stringify(savedVersion.targeting_filters) !==
				JSON.stringify(draftVersion.targeting_filters) ||
			JSON.stringify(savedVersion.manual_inclusion_filters) !==
				JSON.stringify(draftVersion.manual_inclusion_filters) ||
			JSON.stringify(savedVersion.manual_exclusion_filters) !==
				JSON.stringify(draftVersion.manual_exclusion_filters) ||
			JSON.stringify(savedVersion.targeted_areas) !== JSON.stringify(draftVersion.targeted_areas) ||
			savedVersion.targeting_type !== draftVersion.targeting_type
		)
	}

	const hasSelectedDifferentVersion = () => {
		if (!selectedVersionId || !versions.length) return false
		const activeVersion = versions.find((v) => v.status === 'active')
		return selectedVersionId !== activeVersion?.uuid
	}

	const disableSave =
		(isAdding && !grid?.name?.trim()) ||
		(!isAdding &&
			!hasVersionChanges() &&
			!hasGridMetadataChanges() &&
			!hasSelectedDifferentVersion())

	const handleButtonClick = () => {
		if (isAdding) {
			handleCreateGrid()
		} else if (hasSelectedDifferentVersion()) {
			handlePromoteSelectedVersion()
		} else if (hasVersionChanges()) {
			setIsCreateVersionModalOpen(true)
		} else if (hasGridMetadataChanges()) {
			handleSaveGridMetadata()
		}
	}

	const handleCreateGrid = async () => {
		if (!grid?.name?.trim()) {
			enqueueSnackbar('Grid name required', { variant: 'error' })
			return
		}
		try {
			// Create new grid
			const newGrid = await createGrid({
				workspaceId: organization?.organization_id,
				grid: { ...grid, currentVersion: draftVersion! },
			})
			enqueueSnackbar('Grid created successfully', { variant: 'success' })
			navigate(`/${workspaceId}/grids/${newGrid.uuid}`)
		} catch (error) {
			console.error('Failed to create grid:', error)
			enqueueSnackbar('Failed to create grid', { variant: 'error' })
		}
	}

	const handleSaveGridMetadata = async () => {
		if (!grid?.name?.trim()) {
			enqueueSnackbar('Grid name required', { variant: 'error' })
			return
		}
		try {
			// Update only grid metadata (name, etc.)
			await updateGrid({
				workspaceId: organization?.organization_id,
				grid: { ...grid, id: gridId },
			})
			enqueueSnackbar('Grid saved successfully', { variant: 'success' })
		} catch (error) {
			console.error('Failed to save grid:', error)
			enqueueSnackbar('Failed to save grid', { variant: 'error' })
		}
	}

	const handlePromoteSelectedVersion = async () => {
		if (!selectedVersionId) {
			enqueueSnackbar('No version selected', { variant: 'error' })
			return
		}
		try {
			// Promote the selected version to active
			const promotedVersion = await promoteGridVersion({
				workspaceId: organization?.organization_id,
				gridId: gridId,
				versionId: selectedVersionId,
			})

			// Update Redux state to reflect the promoted version
			dispatch(
				setGrid({
					...grid!,
					currentVersion: promotedVersion,
				})
			)

			enqueueSnackbar('Version activated successfully', { variant: 'success' })
		} catch (error) {
			console.error('Failed to activate version:', error)
			enqueueSnackbar('Failed to activate version', { variant: 'error' })
		}
	}

	const handleCreateVersion = async (versionName: string) => {
		if (!grid?.name?.trim()) {
			enqueueSnackbar('Grid name required', { variant: 'error' })
			return
		}
		try {
			// 1. Update grid metadata (name, etc.)
			await updateGrid({
				workspaceId: workspaceId,
				grid: { ...grid, id: gridId },
			})

			// 2. Create new version with draft changes and name
			await createGridVersion({
				workspaceId: organization?.organization_id,
				gridId,
				version: { ...draftVersion!, name: versionName },
			})

			enqueueSnackbar('Version created successfully', { variant: 'success' })
		} catch (error) {
			console.error('Failed to create version:', error)
			enqueueSnackbar('Failed to create version', { variant: 'error' })
		}
	}

	const handleCreateAndActivateVersion = async (versionName: string) => {
		if (!grid?.name?.trim()) {
			enqueueSnackbar('Grid name required', { variant: 'error' })
			return
		}
		try {
			// 1. Update grid metadata (name, etc.)
			await updateGrid({
				workspaceId: workspaceId,
				grid: { ...grid, id: gridId },
			})

			// 2. Create new version with draft changes and name
			const newVersion = await createGridVersion({
				workspaceId,
				gridId,
				version: { ...draftVersion!, name: versionName },
			})

			// 3. Promote the new version to active
			const promotedVersion = await promoteGridVersion({
				workspaceId,
				gridId,
				versionId: newVersion.uuid,
			})

			// 4. Update Redux state to reflect the saved changes
			dispatch(
				setGrid({
					...grid,
					currentVersion: promotedVersion,
				})
			)

			enqueueSnackbar('Version created and activated successfully', { variant: 'success' })
		} catch (error) {
			console.error('Failed to create and activate version:', error)
			enqueueSnackbar('Failed to create and activate version', { variant: 'error' })
		}
	}

	return (
		<SidePanel
			header={{
				title: 'Grids',
				backEnabled: true,
				onBackClicked() {
					dispatch(resetGrid())
				},
			}}
			footer={{
				buttonLabel: 'Save grid',
				handler: handleButtonClick,
				disabled: disableSave,
			}}
		>
			<GridSideControllerContainer />
			<CreateVersionModal
				open={isCreateVersionModalOpen}
				onClose={() => setIsCreateVersionModalOpen(false)}
				onCreateVersion={handleCreateVersion}
				onCreateAndActivate={handleCreateAndActivateVersion}
			/>
		</SidePanel>
	)
}
